import type { PageJson } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';
import { buildCssVarStyle, imageVarIndices } from '@apps/card/utils/colorway';
import { absolutePhotoUrl, applyRenditionIfMediaServiceUrl } from '@apps/card/utils/mediaService';
import type { Rendition } from '@apps/card/utils/mediaService';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Resolves a colorway selection into inline CSS custom properties (`--N`)
 * for live preview, without mutating the draft (the saved draft keeps its
 * `var(--N)` tokens so the backend collapses them at render time).
 */
export function useColorways(page: PageJson | undefined, rendition?: Rendition, colorwayOverride?: string) {
  const colorways = page?.colorways;
  const options = colorways?.options;
  const [selectedId, setSelectedId] = useState<string>(() => {
    if (colorwayOverride && options?.some(o => o.id === colorwayOverride)) return colorwayOverride;
    if (colorways?.selectedId && options?.some(o => o.id === colorways.selectedId)) return colorways.selectedId;
    return options?.[0]?.id ?? '';
  });

  // Components consuming the override don't remount when it changes (e.g. a
  // gallery tile surviving a search-term change), so sync it post-mount too.
  // Keyed on the override value only: a manual swatch selection is not
  // clobbered while the same override stays active.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (colorwayOverride) {
      if (options?.some(o => o.id === colorwayOverride)) setSelectedId(colorwayOverride);
    } else {
      // Override cleared (e.g. search reset): return to the catalog default.
      setSelectedId(colorways?.selectedId && options?.some(o => o.id === colorways.selectedId) ? colorways.selectedId : (options?.[0]?.id ?? ''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colorwayOverride]);

  const selectedColorway = options?.find(o => o.id === selectedId)?.values;

  const imageIndices = useMemo(() => imageVarIndices(page?.layers ?? []), [page?.layers]);

  const cssVarStyle = useMemo(() => (selectedColorway ? buildCssVarStyle(selectedColorway, imageIndices, rendition) : {}), [selectedColorway, imageIndices, rendition]);

  // Preload all colorway images on first hover so swatch switches are instant.
  const hasPreloaded = useRef(false);
  const preloadImages = useCallback(() => {
    if (hasPreloaded.current || !options) return;
    hasPreloaded.current = true;
    for (const { values } of options) {
      imageIndices.forEach(index => {
        const value = values[index];
        if (value) new Image().src = applyRenditionIfMediaServiceUrl(absolutePhotoUrl(value), rendition);
      });
    }
  }, [options, imageIndices, rendition]);

  return { options, selectedId, setSelectedId, selectedColorway, cssVarStyle, preloadImages };
}
